/**
 * Unified Compliance Slide-over Alpine.js Component
 *
 * Parameterised component for both Gas Safety and EICR compliance.
 * Displays property compliance status with:
 * - Current certificate thumbnail and details
 * - Timeline of all certificates with gap analysis
 * - Workflow actions (send reminder, raise the renewal, etc.)
 *
 * Usage in ERB:
 *   x-data="complianceSlideover({ type: 'gas_safety', label: 'Gas Safety', ... })"
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'

export function complianceSlideover(config) {
  return {
    ...notesMixin,
    // Config (passed from ERB)
    config,

    // State
    open: false,
    loading: false,
    error: null,
    propertyId: null,
    property: null,
    currentCertificate: null,
    certificates: [],
    complianceNotes: [],
    complianceReminders: [],
    jobs: [],
    coverageAnalysis: null,
    hasActiveTenancy: false,
    pendingReminder: null,
    reminderResponding: false,
    workflowStatus: null,
    workflowActionLoading: false,
    workflowActionKey: null,
    subscription: null,
    zIndex: 100010,
    isRotating: false,
    showOlderCertificates: false,
    // The timeline cut to the certificates and the gaps between them
    certificatesOnly: false,
    noteForm: { content: '', saving: false },
    timeline: [],
    activeTab: 'certificate',

    init() {
      const self = this
      const logPrefix = `[ComplianceSlideover:${this.config.label}]`

      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            if (message.type === 'certificate' && self.property && message.data?.property_id === self.property.id) {
              console.debug(logPrefix, 'Received certificate update for property', self.property.id)
              self.fetchCompliance(true)
            }
            if (message.type === 'compliance_note' && self.property && message.data?.property_id === self.property.id) {
              if (message.action === 'create') {
                const idx = self.complianceNotes.findIndex(n => n.id === message.data.id)
                if (idx >= 0) {
                  self.complianceNotes[idx] = { ...self.complianceNotes[idx], ...message.data }
                  self.rebuildTimeline()
                } else {
                  self.complianceNotes.unshift(message.data)
                  self.rebuildTimeline()
                }
              } else if (message.action === 'destroy') {
                self.complianceNotes = self.complianceNotes.filter(n => n.id !== message.id)
                self.rebuildTimeline()
              }
            }
            if (message.type === 'renewal' && self.property && message.data?.property_id === self.property.id) {
              console.debug(logPrefix, 'Received renewal update for property', self.property.id)
              self.fetchCompliance(true)
            }
            if (message.type === 'job_event' && self.property && message.data?.property_id === self.property.id) {
              console.debug(logPrefix, 'Received job_event update for property', self.property.id)
              self.fetchCompliance(true)
            }
            if (message.type === 'job_invitation' && self.property && message.data?.property_id === self.property.id) {
              console.debug(logPrefix, 'Received job_invitation update for property', self.property.id)
              self.fetchCompliance(true)
            }
            if (message.type === 'compliance_reminder' && self.property && message.data?.property_id === self.property.id) {
              console.debug(logPrefix, 'Received compliance_reminder update for property', self.property.id)
              self.fetchCompliance(true)
            }
          }
        }
      )

      this.$watch('open', (value) => {
        if (value && this.propertyId && !this.property) {
          this.fetchCompliance()
        }
      })

      this.$watch('showOlderCertificates', () => {
        this.rebuildTimeline()
      })
    },

    openForProperty(propertyId) {
      const store = Alpine.store?.('slideovers')

      if (store?.isOpen?.(this.config.storeKey, propertyId)) {
        store.bringToFront(this.config.storeKey, propertyId)
        return
      }

      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.propertyId = propertyId
      this.property = null
      this.currentCertificate = null
      this.certificates = []
      this.complianceNotes = []
      this.coverageAnalysis = null
      this.error = null
      this.showOlderCertificates = false
      this.certificatesOnly = false
      this.noteForm = { content: '', saving: false }
      this.open = true

      if (store?.register) {
        store.register(this.config.storeKey, propertyId, this)
      }

      this.fetchCompliance()
    },

    close() {
      const store = Alpine.store('slideovers')
      if (store && this.propertyId) {
        store.unregister(this.config.storeKey, this.propertyId)
      }
      this.open = false
    },

    async fetchCompliance(silent = false) {
      if (!this.propertyId) return

      if (!silent) {
        this.loading = true
      }
      this.error = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/${this.config.endpoint}.json`, {
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.property = data.property
        this.currentCertificate = data.current_certificate
        this.certificates = data.certificates || []
        this.complianceNotes = data.compliance_notes || []
        this.complianceReminders = data.compliance_reminders || []
        this.jobs = data.jobs || []
        this.coverageAnalysis = data.coverage_analysis
        this.hasActiveTenancy = data.has_active_tenancy
        this.pendingReminder = data.pending_reminder || null
        this.workflowStatus = data.workflow_status || null
        this.rebuildTimeline()
      } catch (error) {
        console.error(`Failed to fetch ${this.config.label} compliance:`, error)
        this.error = 'Failed to load compliance details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Whether the main content should be visible (gas checks for gas supply)
    get contentVisible() {
      if (!this.property) return false
      if (this.config.hasGasSupplyCheck && this.property.has_gas_supply === false) return false
      return true
    },

    get complianceStatusClass() {
      if (this.config.hasGasSupplyCheck && this.property && !this.property.has_gas_supply) {
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
      }
      if (!this.currentCertificate) {
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      }
      if (this.currentCertificate.days_until_expiry <= 0) {
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      }
      if (this.currentCertificate.days_until_expiry <= 30) {
        return 'bg-warning-100 text-warning-600 dark:bg-warning-500/20 dark:text-warning-500'
      }
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    },

    get statusBorderColor() {
      if (this.config.hasGasSupplyCheck && this.property && !this.property.has_gas_supply) {
        return 'border-gray-400'
      }
      if (!this.currentCertificate) {
        return 'border-red-500'
      }
      if (this.currentCertificate.days_until_expiry <= 0) {
        return 'border-red-500'
      }
      if (this.currentCertificate.days_until_expiry <= 30) {
        return 'border-[#F2BD5B]'
      }
      return 'border-emerald-400'
    },

    get complianceStatusLabel() {
      if (this.config.hasGasSupplyCheck && this.property && !this.property.has_gas_supply) {
        return 'No Gas Supply'
      }
      if (!this.currentCertificate) {
        return 'No Certificate'
      }
      if (this.currentCertificate.days_until_expiry <= 0) {
        return 'Expired'
      }
      if (this.currentCertificate.days_until_expiry <= 30) {
        return 'Expiring Soon'
      }
      return 'Valid'
    },

    get coverageReasonText() {
      if (!this.coverageAnalysis?.coverage_reason) return null

      const tenantNames = this.coverageAnalysis.tenant_names
      const hasTenantNames = tenantNames && tenantNames.length > 0
      const tenancyStartDate = this.coverageAnalysis.tenancy_start_date
        ? new Date(this.coverageAnalysis.tenancy_start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : null

      if (this.coverageAnalysis.coverage_reason === 'new_tenancy') {
        if (hasTenantNames) {
          return `${tenantNames.join(' & ')}'s tenancy started`
        }
        return 'Tenancy started on this date'
      }
      if (this.coverageAnalysis.coverage_reason === 'existing_tenancy') {
        if (hasTenantNames && tenancyStartDate) {
          return `${tenantNames.join(' & ')}'s tenancy started ${tenancyStartDate}`
        }
        if (tenancyStartDate) {
          return `Tenancy started ${tenancyStartDate}`
        }
        return 'Tenancy started before July 2020'
      }
      return null
    },

    get certificatesWithoutDocumentsCount() {
      const coverageStart = this.coverageAnalysis?.coverage_start
        ? new Date(this.coverageAnalysis.coverage_start)
        : null

      return this.certificates.filter(cert => {
        if (cert.has_documents) return false
        if (!coverageStart) return true
        if (!cert.expiry_date) return true
        return new Date(cert.expiry_date) >= coverageStart
      }).length
    },

    // The timeline as shown: everything, or the certificate history alone,
    // read straight through for the tenancy (Paul, 22 Oct 2026)
    get shownTimeline() {
      if (!this.certificatesOnly) return this.timeline
      return this.timeline.filter(item => item.itemType === 'certificate' || item.itemType === 'gap')
    },

    get olderCertificatesCount() {
      if (!this.coverageAnalysis?.coverage_start) return 0
      const coverageStart = new Date(this.coverageAnalysis.coverage_start)
      return this.certificates.filter(cert => {
        if (!cert.expiry_date) return false
        return new Date(cert.expiry_date) < coverageStart
      }).length
    },

    formatTimeRemaining(days) {
      if (days === null || days === undefined) return ''
      if (days < 0) return `${Math.abs(days)} days overdue`
      if (days === 0) return 'Expires today'
      if (days === 1) return '1 day remaining'
      if (days <= 30) return `${days} days remaining`
      if (days <= 60) return `${Math.floor(days / 7)} weeks remaining`
      const months = Math.floor(days / 30)
      if (months < 24) return `${months} ${months === 1 ? 'month' : 'months'} remaining`
      const years = Math.floor(days / 365)
      return `${years} ${years === 1 ? 'year' : 'years'} remaining`
    },

    formatGapDuration(days) {
      if (days <= 0) return ''
      if (days === 1) return '1 day'
      if (days < 30) return `${days} days`
      if (days < 60) return `${Math.floor(days / 7)} weeks`
      const months = Math.floor(days / 30)
      if (months < 24) return `${months} ${months === 1 ? 'month' : 'months'}`
      const years = Math.floor(days / 365)
      return `${years} ${years === 1 ? 'year' : 'years'}`
    },

    openAddCertificateModal() {
      if (!this.property) return
      window.dispatchEvent(new CustomEvent('open-certificate-modal', {
        detail: {
          mode: 'create',
          property_id: this.property.id,
          property_address: this.property.display_name,
          certificate_type_id: this.getCertificateTypeId()
        }
      }))
    },

    openAddCertificateModalWithFile(file) {
      if (!this.property) return
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PDF, JPG, or PNG file.')
        return
      }
      window.dispatchEvent(new CustomEvent('open-certificate-modal', {
        detail: {
          mode: 'create',
          property_id: this.property.id,
          property_address: this.property.display_name,
          certificate_type_id: this.getCertificateTypeId(),
          analyse_file: file
        }
      }))
    },

    openEditCertificateModal(certificateId = null) {
      const certId = certificateId || this.currentCertificate?.id
      if (!certId || !this.property) return

      const cert = this.certificates.find(c => c.id === certId)
      if (!cert) return

      window.dispatchEvent(new CustomEvent('open-certificate-modal', {
        detail: {
          mode: 'edit',
          id: cert.id,
          property_id: this.property.id,
          property_address: this.property.display_name,
          certificate_type_id: cert.certificate_type_id,
          issue_date: cert.issue_date,
          expiry_date: cert.expiry_date,
          certificate_number: cert.certificate_number,
          contractor_name: cert.contractor_name,
          contractor_company: cert.contractor_company,
          contractor_registration_number: cert.contractor_registration_number,
          contractor_phone: cert.contractor_phone,
          contractor_email: cert.contractor_email,
          contractor_id: cert.contractor_id,
          responsible_party: cert.responsible_party,
          external_document_url: cert.external_document_url,
          notes: cert.notes
        }
      }))
    },

    getCertificateTypeId() {
      // Prefer current certificate, fall back to any certificate in the list,
      // then look up by slug from the certificate modal's embedded data
      if (this.currentCertificate?.certificate_type_id) {
        return this.currentCertificate.certificate_type_id
      }
      if (this.certificates.length > 0) {
        return this.certificates[0].certificate_type_id
      }
      // Look up from the certificate modal's embedded types data
      const modalEl = document.querySelector('[data-certificate-types]')
      if (modalEl) {
        try {
          const raw = modalEl.dataset.certificateTypes.replace(/&quot;/g, '"')
          const types = JSON.parse(raw)
          const match = types.find(t => t.slug === this.config.slug)
          if (match) return match.id
        } catch (_e) { /* fall through */ }
      }
      return null
    },

    viewDocument(url) {
      if (url) window.open(url, '_blank')
    },

    async rotateDocument(delta) {
      if (!this.currentCertificate?.documents?.[0]) return
      if (this.isRotating) return

      const doc = this.currentCertificate.documents[0]
      this.isRotating = true

      try {
        const response = await fetch(
          `/properties/${this.property.id}/certificates/${this.currentCertificate.id}/documents/${doc.id}/rotate`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            },
            body: JSON.stringify({ delta: delta })
          }
        )
        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.error || 'Failed to rotate document')
        }
        await this.fetchCompliance(true)
      } catch (error) {
        console.error('Failed to rotate document:', error)
        alert(error.message || 'Failed to rotate document. Please try again.')
      } finally {
        this.isRotating = false
      }
    },

    async addNote() {
      if (!this.noteForm.content?.trim()) return
      if (!this.property) return

      this.noteForm.saving = true
      try {
        // Compliance notes are scoped to the certificate type as well as the property
        const certificateTypeId = this.getCertificateTypeId()
        if (certificateTypeId && !this.noteLinks.some(l => l.type === 'CertificateType')) {
          this.noteLinks.push({ type: 'CertificateType', id: certificateTypeId, label: this.config.label })
        }
        const newNote = await this.saveNote({ type: 'Property', id: this.property.id }, this.noteForm.content)
        const exists = this.complianceNotes.some(n => n.id === newNote.id)
        if (!exists) {
          this.complianceNotes.unshift(newNote)
          this.rebuildTimeline()
        }
        this.noteForm.content = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        alert(error.message || 'Failed to add note. Please try again.')
      } finally {
        this.noteForm.saving = false
      }
    },
    removeTimelineEntry(entryId) {
      this.complianceNotes = this.complianceNotes.filter(n => n.id !== entryId)
      this.rebuildTimeline()
    },

    async deleteNote(itemId) {
      if (!confirm('Delete this note?')) return
      try {
        // Timeline items are prefixed "note-"; the note keeps its "nt_" id beneath it
        const noteId = String(itemId).replace(/^note-/, '')
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    async respondToReminder(optionIndex) {
      if (!this.pendingReminder) return
      if (this.reminderResponding) return

      this.reminderResponding = true
      try {
        const response = await fetch(`/compliance_reminders/${this.pendingReminder.id}/respond`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ option_index: optionIndex })
        })

        if (response.ok) {
          const data = await response.json()
          if (data.compliance_data) {
            this.property = data.compliance_data.property
            this.currentCertificate = data.compliance_data.current_certificate
            this.certificates = data.compliance_data.certificates || []
            this.complianceNotes = data.compliance_data.compliance_notes || []
            this.complianceReminders = data.compliance_data.compliance_reminders || []
            this.jobs = data.compliance_data.jobs || []
            this.coverageAnalysis = data.compliance_data.coverage_analysis
            this.hasActiveTenancy = data.compliance_data.has_active_tenancy
            this.pendingReminder = data.compliance_data.pending_reminder || null
            this.workflowStatus = data.compliance_data.workflow_status || null
            this.rebuildTimeline()
          }
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to record response')
        }
      } catch (error) {
        console.error('Failed to respond to reminder:', error)
        alert('Failed to record response. Please try again.')
      } finally {
        this.reminderResponding = false
      }
    },

    async handleWorkflowAction(actionKey) {
      if (this.workflowActionLoading) return

      const action = this.workflowStatus?.actions?.find(a => a.key === actionKey)
      if (action?.coming_soon) {
        alert('This feature is coming soon')
        return
      }

      this.workflowActionLoading = true
      this.workflowActionKey = actionKey

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        let endpoint = null
        switch (actionKey) {
          case 'book_now':
            endpoint = `/properties/${this.property.id}/compliance/${this.config.slug}/book_job`
            break
          case 'send_reminder':
            endpoint = `/properties/${this.property.id}/compliance/${this.config.slug}/send_reminder`
            break
          case 'landlord_arranging':
            endpoint = `/properties/${this.property.id}/compliance/${this.config.slug}/landlord_arranging`
            break
          default:
            console.warn(`[ComplianceSlideover:${this.config.label}] Unknown action:`, actionKey)
            return
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        })

        const data = await response.json()

        if (response.ok && data.success) {
          if (data.compliance_data) {
            this.property = data.compliance_data.property
            this.currentCertificate = data.compliance_data.current_certificate
            this.certificates = data.compliance_data.certificates || []
            this.complianceNotes = data.compliance_data.compliance_notes || []
            this.complianceReminders = data.compliance_data.compliance_reminders || []
            this.jobs = data.compliance_data.jobs || []
            this.coverageAnalysis = data.compliance_data.coverage_analysis
            this.hasActiveTenancy = data.compliance_data.has_active_tenancy
            this.pendingReminder = data.compliance_data.pending_reminder || null
            this.workflowStatus = data.compliance_data.workflow_status || null
            this.rebuildTimeline()
          }
          // The renewal just raised opens so the office can instruct a contractor
          if (data.renewal_id) {
            window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { renewal_id: data.renewal_id } }))
          }
        } else {
          alert(data.error || 'Failed to perform action')
        }
      } catch (error) {
        console.error(`[ComplianceSlideover:${this.config.label}] Action failed:`, error)
        alert('Failed to perform action. Please try again.')
      } finally {
        this.workflowActionLoading = false
        this.workflowActionKey = null
      }
    },

    rebuildTimeline() {
      const items = []
      const coverageStart = this.coverageAnalysis?.coverage_start
        ? new Date(this.coverageAnalysis.coverage_start)
        : null

      // Gaps
      if (this.coverageAnalysis?.gaps) {
        this.coverageAnalysis.gaps.forEach((gap, index) => {
          items.push({
            ...gap,
            id: `gap-${index}-${gap.start_date}`,
            itemType: 'gap',
            sortDate: new Date(gap.start_date)
          })
        })
      }

      // Certificates
      this.certificates.forEach(cert => {
        if (coverageStart && !this.showOlderCertificates) {
          if (cert.expiry_date && new Date(cert.expiry_date) < coverageStart) return
        }
        items.push({
          ...cert,
          id: `cert-${cert.id}`,
          certId: cert.id,
          itemType: 'certificate',
          sortDate: cert.issue_date ? new Date(cert.issue_date) : new Date(0)
        })
      })

      // Notes
      this.complianceNotes.forEach(note => {
        items.push({
          ...note,
          id: `note-${note.id}`,
          itemType: 'note',
          sortDate: new Date(note.created_at)
        })
      })

      // Reminders (with follow-ups, chases, responses, confirmations as separate entries)
      this.complianceReminders.forEach(reminder => {
        items.push({
          ...reminder,
          id: `reminder-${reminder.id}`,
          itemType: 'reminder',
          sortDate: new Date(reminder.sent_at)
        })

        if (reminder.followup_sent_at) {
          items.push({
            ...reminder,
            id: `followup-${reminder.id}`,
            itemType: 'followup',
            sortDate: new Date(reminder.followup_sent_at)
          })
        }

        if (reminder.landlord_chase_sent_at) {
          items.push({
            ...reminder,
            id: `landlord-chase-${reminder.id}`,
            itemType: 'landlord_chase',
            sortDate: new Date(reminder.landlord_chase_sent_at)
          })
        }

        if (reminder.responded_at) {
          items.push({
            ...reminder,
            id: `response-${reminder.id}`,
            itemType: 'response',
            sortDate: new Date(reminder.responded_at)
          })

          if (reminder.confirmation_sent_at) {
            items.push({
              ...reminder,
              id: `confirmation-${reminder.id}`,
              itemType: 'confirmation',
              sortDate: new Date(reminder.confirmation_sent_at)
            })
          }
        }

        if (reminder.auto_booked_at) {
          items.push({
            ...reminder,
            id: `auto-booked-${reminder.id}`,
            itemType: 'auto_booked',
            sortDate: new Date(reminder.auto_booked_at)
          })
        }

        if (reminder.landlord_clicked_at) {
          items.push({
            ...reminder,
            id: `late-click-${reminder.id}`,
            itemType: 'late_click',
            sortDate: new Date(reminder.landlord_clicked_at)
          })
        }
      })

      // Job events
      this.jobs.forEach(job => {
        if (job.events && job.events.length > 0) {
          const hasInvitationSent = job.events.some(e => e.event_type === 'invitation_sent')
          job.events.forEach(event => {
            if (event.event_type === 'created' && hasInvitationSent) return
            items.push({
              ...event,
              id: `job-event-${event.id}`,
              itemType: 'job_event',
              jobTitle: job.title,
              jobStatus: job.status,
              jobStatusLabel: job.status_label,
              sortDate: new Date(event.occurred_at)
            })
          })
        } else {
          items.push({
            ...job,
            id: `job-${job.id}`,
            itemType: 'job_event',
            jobTitle: job.title,
            jobStatus: job.status,
            jobStatusLabel: job.status_label,
            display_message: 'Renewal raised',
            event_icon: 'plus-circle',
            event_color: 'blue',
            sortDate: new Date(job.created_at)
          })
        }
      })

      // Reverse-chronological. Tiebreaker for items at the exact same
      // timestamp: lower storyOrder means "happens earlier in the story",
      // so it ends up lower in the rendered list. The auto-book is
      // logically what triggers the contractor invitation, so when both
      // land in the same minute it must appear after (i.e. below) the
      // job event.
      const storyOrder = (item) => {
        if (item.itemType === 'auto_booked') return 0
        if (item.itemType === 'job_event') return 1
        return 0
      }
      items.sort((a, b) => {
        const dateDiff = b.sortDate - a.sortDate
        if (dateDiff !== 0) return dateDiff
        return storyOrder(b) - storyOrder(a)
      })
      this.timeline = items
    }
  }
}
